<template>
  <Modal
    title="Inviter des Designers"
    class-name="large"
    :close-icon="closeIcon"
    :is-loading="isLoading"
    :is-visible="true"
  >
    <template v-slot:body>
      <div v-if="!invitesSendSuccess">
        <p class="text-black">
          <span v-html="designerCountMessage"></span>
          <span
            v-if="invite.BasePaymentCount > 0"
            v-html="basePaymentCountMessage"
          >
          </span>
        </p>
        <Callout
          v-if="isTooManyParticipationPayments"
          type="warning"
          :icon-url="warningIcon"
          title="Trop de paiements de participation alloués"
          :closable="true"
          @close="isTooManyParticipationPayments = false"
        >
          <template v-slot:message>
            Vous n'avez que {{ invite.BasePaymentCount }} paiement(s) de participation à allouer. Désélectionnez le paiement de participation d'un autre designer avant d'essayer d'attribuer
          </template>
        </Callout>
        <div v-if="inviteData" class="table-scroll-modal">
          <table class="table stack">
            <thead class="hide-for-small-only hide-for-medium-only">
              <tr>
                <th>Designer</th>
                <th>Envoyer une invitation ?</th>
                <th v-if="invite.BasePaymentCount > 0">
                  Envoyez une prime de participation ?
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(designer, index) in inviteData.Designers"
                :key="index"
              >
                <td>
                  <div class="user-profile user-profile--tiny mhn">
                    <div class="row collapse align-middle mhn">
                      <div class="column shrink">
                        <div
                          v-if="designer.ProfilePicUrl"
                          class="user-profile__image"
                        >
                          <div class="responsive-embed square">
                            <img
                              class="responsive-embed__image"
                              height="40"
                              width="40"
                              :src="designer.ProfilePicUrl"
                              alt="Profile picture"
                            />
                          </div>
                        </div>
                      </div>
                      <div class="column">
                        <div class="row">
                          <div class="column small-12">
                            <span class="user-profile__small-text">
                              <a :href="designer.PortfolioUrl">
                                {{ designer.DisplayName }}
                              </a>
                            </span>
                          </div>
                          <div
                            v-if="designer.CountryName"
                            class="column small-12"
                          >
                            <span class="user-profile__small-text">
                              {{ designer.CountryName }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <Checkbox
                    :id="`invite-designer-${designer.DesignerId}`"
                    :checked="invitedDesigners[index].isInvited"
                    @input="onInviteDesignerChecked($event, index)"
                  >
                    Inviter au projet
                  </Checkbox>
                </td>
                <td v-if="invite.BasePaymentCount">
                  <Checkbox
                    :id="`send-participation-payment-${designer.DesignerId}`"
                    :checked="invitedDesigners[index].isPaidInvite"
                    @input="onParticipationPaymentChecked($event, index)"
                  >
                    prime de participation
                  </Checkbox>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="text-center">
          <Checkbox id="reallocate-invites" @input="reallocateInvites = $event">
            Réaffecter les invitations en cas de rejet
          </Checkbox>
        </div>
      </div>
      <div v-else>
        <Callout
          type="success"
          :icon-url="successIcon"
          title="Invitation envoyée avec succès"
          @close="isTooManyParticipationPayments = false"
        >
          <template v-slot:message>
            Nous avons envoyé {{ invitedDesignersCount }} invitation(s) en votre nom.
          </template>
        </Callout>
        <p>
          Vous pouvez également choisir manuellement et envoyer des invitations aux designers de notre répertoire de designers. Pour cela, il suffit de :
        </p>
        <ol>
          <li>
            <span v-html="designerPortfolioMessage"></span>
          </li>
          <li>
            Trouvez un designer que vous souhaitez inviter personnellement à votre projet
          </li>
          <li>
            <span v-html="getADesignFromMeMessage"></span>
            <img
              :src="inviteDesignerPortfolioBanner"
              alt="Invite Designer Portfolio Banner"
            />
          </li>
          <li>
            <span v-html="forMoreInfoMessage"></span>
          </li>
        </ol>
      </div>
    </template>
    <template v-slot:footer>
      <div v-if="!invitesSendSuccess">
        <input
          type="submit"
          :class="{
            ['button small mrm']: true,
            disabled: invitedDesignersCount === 0,
          }"
          value="Envoyez des invitations"
          @click="onClickSubmitInvites"
        />
        <button
          class="button secondary hollow small"
          data-close
          data-role="cancel-invite-wizard"
        >
          Annuler
        </button>
      </div>
      <div v-else>
        <button class="button small" data-close>OK, j'ai compris</button>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from '../../../shared/Modal/Modal.vue';
import Checkbox from '../../../shared/Checkbox/Checkbox.vue';
import Callout from '../../../shared/Callout/Callout.vue';
import submitInvitesAsync from '../../../../clients/invites-api';

export default {
  components: {
    Modal,
    Checkbox,
    Callout,
  },
  props: {
    warningIcon: {
      type: String,
      default: undefined,
    },
    successIcon: {
      type: String,
      default: undefined,
    },
    invite: {
      type: Object,
      required: true,
    },
    closeIcon: {
      type: String,
      default: undefined,
    },
    inviteDesignerPortfolioBanner: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      invitedDesigners: [],
      inviteData: null,
      isTooManyParticipationPayments: false,
      reallocateInvites: false,
      invitesSendSuccess: false,
      isLoading: false,
    };
  },
  computed: {
    designerCount() {
      return this.invite.Designers.length;
    },
    invitedDesignersCount() {
      return this.invitedDesigners.filter((payment) => payment.isInvited)
        .length;
    },
    briefId() {
      const urlMatch = this.$route
        ? this.$route.fullPath.match(/([^/]+$)/)
        : null;

      if (urlMatch && urlMatch.length > 1) {
        return parseInt(urlMatch[1], 10);
      }

      return null;
    },
    designerCountMessage() {
      return `Nous vous recommandons d'inviter les <strong>${this.designerCount} designers</strong> suivants à votre projet.`;
    },
    basePaymentCountMessage() {
      return `Vous pouvez allouer <strong>${this.invite.BasePaymentCount} paiements de participation (des invitations payées) pour inciter les meilleurs designers à travailler sur votre projet.</Strong>`;
    },
    getADesignFromMeMessage() {
      return `Cliquez sur <strong>Obtenez un design de ma part</strong> dans le portfolio d'un designer et suivez les instructions`;
    },
    forMoreInfoMessage() {
      return `Pour plus d'informations, rendez-vous sur <a href='${this.invite.ForMoreInfoUrl}'>Comment inviter des designers à mon projet ?</a>`;
    },
    designerPortfolioMessage() {
      return `Accédez au portfolio du designer - cliquez sur le lien <a href='${this.invite.DesignersListUrl}'> Designers</a> dans la barre de navigation de DesignCrowd`;
    },
  },
  mounted() {
    this.initialiseInvitedDesigners();
    this.inviteData = this.invite;
  },
  methods: {
    initialiseInvitedDesigners() {
      this.invitedDesigners = this.invite.Designers.map((designer) => ({
        designerId: designer.DesignerId,
        isPaidInvite: designer.RecommendedForPaidInvite,
        isInvited: true,
      }));
    },
    onInviteDesignerChecked(checked, index) {
      this.$set(this.invitedDesigners[index], 'isInvited', checked);
    },
    onParticipationPaymentChecked(checked, index) {
      this.$set(this.invitedDesigners[index], 'isPaidInvite', checked);
      const checkedPayments = this.invitedDesigners.filter(
        (x) => x.isPaidInvite,
      );

      if (
        checkedPayments &&
        checkedPayments.length > this.inviteData.BasePaymentCount
      ) {
        this.$nextTick(() => {
          this.$set(this.invitedDesigners[index], 'isPaidInvite', false);
          this.isTooManyParticipationPayments = true;
        });
      } else {
        this.isTooManyParticipationPayments = false;
      }
    },
    async onClickSubmitInvites() {
      if (this.invitedDesignersCount > 0) {
        const invites = {
          autoReallocate: this.reallocateInvites,
          designers: this.invitedDesigners.filter(
            (designer) => designer.isInvited,
          ),
        };

        this.isLoading = true;
        const res = await submitInvitesAsync(this.briefId, invites);
        this.invitesSendSuccess = res;
        this.isLoading = false;
      }
    },
  },
};
</script>
